import type { HHMM, ISODate } from './types';

const DAG_SLEUTELS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'] as const;
export type DagSleutel = (typeof DAG_SLEUTELS)[number];

const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const DAGNAMEN_KORT = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const DAGNAMEN = ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag'];

/** Parse 'YYYY-MM-DD' naar UTC-milliseconden (middernacht UTC). */
function ms(d: ISODate): number {
  const [y, m, dd] = d.slice(0, 10).split('-').map(Number);
  return Date.UTC(y, m - 1, dd);
}
function fromMs(t: number): ISODate {
  return new Date(t).toISOString().slice(0, 10);
}

export function vandaag(now: Date = new Date()): ISODate {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDagen(d: ISODate, n: number): ISODate {
  return fromMs(ms(d) + n * 86400000);
}

export function verschilDagen(van: ISODate, tot: ISODate): number {
  return Math.round((ms(tot) - ms(van)) / 86400000);
}

/** 0 = maandag … 6 = zondag */
export function weekdagIndex(d: ISODate): number {
  return (new Date(ms(d)).getUTCDay() + 6) % 7;
}

export function dagSleutel(d: ISODate): DagSleutel {
  return DAG_SLEUTELS[weekdagIndex(d)];
}

export function maandagVan(d: ISODate): ISODate {
  return addDagen(d, -weekdagIndex(d));
}

export function isoWeek(d: ISODate): number {
  const t = new Date(ms(d));
  const dag = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - dag + 3);
  const eersteDonderdag = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((t.getTime() - eersteDonderdag.getTime()) / 86400000 - 3 + ((eersteDonderdag.getUTCDay() + 6) % 7)) / 7);
}

export function reeks(van: ISODate, aantal: number): ISODate[] {
  return Array.from({ length: aantal }, (_, i) => addDagen(van, i));
}

export function tussen(d: ISODate, van?: ISODate | null, tot?: ISODate | null): boolean {
  if (!van) return false;
  return d >= van && d <= (tot ?? van);
}

export function minuten(t?: HHMM | null): number {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

export function hhmm(min: number): HHMM {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Uren tussen twee tijden. */
export function urenTussen(van?: HHMM | null, tot?: HHMM | null): number {
  return Math.max(0, (minuten(tot) - minuten(van)) / 60);
}

export function werkdagenLijst(werkdagen?: string | null): DagSleutel[] {
  const s = (werkdagen || 'ma,di,wo,do,vr').toLowerCase();
  return DAG_SLEUTELS.filter((k) => s.includes(k));
}

/** Nederlandse weergave: 'ma 3 nov', 'maandag 3 nov', '3 nov 2026' */
export function fmtDag(d: ISODate | null | undefined, stijl: 'kort' | 'lang' | 'datum' | 'jaar' = 'kort'): string {
  if (!d) return '';
  const t = new Date(ms(d));
  const dag = t.getUTCDate();
  const mnd = MAANDEN[t.getUTCMonth()];
  const wd = weekdagIndex(d);
  if (stijl === 'lang') return `${DAGNAMEN[wd]} ${dag} ${mnd}`;
  if (stijl === 'datum') return `${dag} ${mnd}`;
  if (stijl === 'jaar') return `${dag} ${mnd} ${t.getUTCFullYear()}`;
  return `${DAGNAMEN_KORT[wd]} ${dag} ${mnd}`;
}

export function rondKwartier(uren: number): number {
  return Math.round(uren * 4) / 4;
}

/** Netjes afronden voor weergave (max. 2 decimalen, Nederlandse komma). */
export function fmtUren(u: number | null | undefined): string {
  const v = Math.round((u ?? 0) * 100) / 100;
  return String(v).replace('.', ',');
}
